/**
 * LongevityDiagram.tsx
 * 
 * Flow diagram showing how clean water leads to longevity:
 * Clean Water → Full Modulation → Reduced Cycling → Extended Component Life
 */

interface LongevityDiagramProps {
  width?: number;
  height?: number;
}

export function LongevityDiagram({ width = 600, height = 200 }: LongevityDiagramProps) {
  const boxWidth = 110;
  const boxHeight = 60;
  const spacing = 20;
  const arrowLength = 35;
  
  // Calculate total width needed for 4 boxes + 3 arrows + spacing
  const totalContentWidth = (boxWidth * 4) + (arrowLength * 3) + (spacing * 6);
  
  // If content is wider than available width, use content width and allow scrolling
  const actualWidth = Math.max(width, totalContentWidth + 40);
  const startX = (actualWidth - totalContentWidth) / 2;
  const centerY = height / 2;

  // Box positions
  const box1X = startX;
  const box2X = box1X + boxWidth + spacing + arrowLength;
  const box3X = box2X + boxWidth + spacing + arrowLength;
  const box4X = box3X + boxWidth + spacing + arrowLength;

  // Arrow positions
  const arrow1X = box1X + boxWidth + spacing;
  const arrow2X = box2X + boxWidth + spacing;
  const arrow3X = box3X + boxWidth + spacing;

  return (
    <svg
      width={actualWidth}
      height={height}
      viewBox={`0 0 ${actualWidth} ${height}`}
      className="dp-longevity-diagram"
      role="img"
      aria-label="Flow diagram showing the foundation of longevity: clean water enables full modulation, which reduces cycling, resulting in extended component life"
    >
      <title>
        Foundation of Longevity: Clean water circuit enables full modulation, 
        which reduces cycling, which extends component life and improves reliability.
      </title>
      <defs>
        <style>{`
          .flow-box { fill: white; stroke: #2563eb; stroke-width: 2; }
          .foundation-box { fill: #dbeafe; stroke: #1e40af; stroke-width: 3; }
          .result-box { fill: #dcfce7; stroke: #16a34a; stroke-width: 3; }
          .flow-text { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; 
            font-size: 13px; 
            font-weight: 600; 
            fill: #1e293b; 
            text-anchor: middle;
          }
          .flow-subtitle { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; 
            font-size: 10px; 
            fill: #64748b; 
            text-anchor: middle;
          }
          .flow-arrow { stroke: #2563eb; stroke-width: 2.5; fill: none; marker-end: url(#arrowhead); }
          .step-label { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; 
            font-size: 11px; 
            font-weight: 600; 
            fill: #2563eb; 
            text-anchor: middle;
          }
        `}</style>
        
        {/* Arrow marker */}
        <marker
          id="arrowhead"
          markerWidth="10"
          markerHeight="10"
          refX="8"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 10 3, 0 6" fill="#2563eb" />
        </marker>
      </defs>

      {/* Step labels above boxes */}
      <text x={box1X + boxWidth / 2} y={centerY - boxHeight / 2 - 25} className="step-label">
        Step 1
      </text>
      <text x={box2X + boxWidth / 2} y={centerY - boxHeight / 2 - 25} className="step-label">
        Step 2
      </text>
      <text x={box3X + boxWidth / 2} y={centerY - boxHeight / 2 - 25} className="step-label">
        Step 3
      </text>
      <text x={box4X + boxWidth / 2} y={centerY - boxHeight / 2 - 25} className="step-label">
        Result
      </text>

      {/* Box 1: Clean Water Circuit (Foundation) */}
      <rect
        x={box1X}
        y={centerY - boxHeight / 2}
        width={boxWidth}
        height={boxHeight}
        rx="6"
        className="foundation-box"
      />
      <text x={box1X + boxWidth / 2} y={centerY - 8} className="flow-text">
        Clean Water
      </text>
      <text x={box1X + boxWidth / 2} y={centerY + 5} className="flow-text">
        Circuit
      </text>
      <text x={box1X + boxWidth / 2} y={centerY + 18} className="flow-subtitle">
        The Foundation
      </text>

      {/* Arrow 1 */}
      <path
        d={`M ${arrow1X} ${centerY} L ${arrow1X + arrowLength} ${centerY}`}
        className="flow-arrow"
      />

      {/* Box 2: Full Modulation */}
      <rect
        x={box2X}
        y={centerY - boxHeight / 2}
        width={boxWidth}
        height={boxHeight}
        rx="6"
        className="flow-box"
      />
      <text x={box2X + boxWidth / 2} y={centerY - 8} className="flow-text">
        Enables Full
      </text>
      <text x={box2X + boxWidth / 2} y={centerY + 5} className="flow-text">
        Modulation
      </text>
      <text x={box2X + boxWidth / 2} y={centerY + 18} className="flow-subtitle">
        Smooth heat delivery
      </text>

      {/* Arrow 2 */}
      <path
        d={`M ${arrow2X} ${centerY} L ${arrow2X + arrowLength} ${centerY}`}
        className="flow-arrow"
      />

      {/* Box 3: Reduced Cycling */}
      <rect
        x={box3X}
        y={centerY - boxHeight / 2}
        width={boxWidth}
        height={boxHeight}
        rx="6"
        className="flow-box"
      />
      <text x={box3X + boxWidth / 2} y={centerY - 8} className="flow-text">
        Reduced
      </text>
      <text x={box3X + boxWidth / 2} y={centerY + 5} className="flow-text">
        Cycling
      </text>
      <text x={box3X + boxWidth / 2} y={centerY + 18} className="flow-subtitle">
        Less mechanical stress
      </text>

      {/* Arrow 3 */}
      <path
        d={`M ${arrow3X} ${centerY} L ${arrow3X + arrowLength} ${centerY}`}
        className="flow-arrow"
      />

      {/* Box 4: Extended Component Life (Result) */}
      <rect
        x={box4X}
        y={centerY - boxHeight / 2}
        width={boxWidth}
        height={boxHeight}
        rx="6"
        className="result-box"
      />
      <text x={box4X + boxWidth / 2} y={centerY - 8} className="flow-text">
        Extended
      </text>
      <text x={box4X + boxWidth / 2} y={centerY + 5} className="flow-text">
        Component Life
      </text>
      <text x={box4X + boxWidth / 2} y={centerY + 18} className="flow-subtitle">
        Improved reliability
      </text>

    </svg>
  );
}
